import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { errMsg } from "@/lib/api";


export default function Login() {
  const { login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { const u = await login(email, password); navigate(u?.role === "super_admin" ? "/vendor" : "/"); }
    catch (err) { setError(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1716191299980-a6e8827ba10b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Factory line" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-lg">
          <div className="pp-label text-blue-400 mb-3">Daily · Locked · Broadcast</div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">Every day, every line, one trusted number.</h1>
          <p className="mt-4 text-slate-200 text-sm leading-relaxed">Replace the daily production Google Sheet with locked submissions, live traffic-light dashboards and one-click Excel / PDF reports you can share anywhere</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 rise" data-testid="login-form">
          <div className="flex items-center gap-3">
            {settings.logo_url ? <img src={settings.logo_url} alt="logo" className="h-11 w-11 rounded-md object-contain bg-white" /> : <div className="flex h-11 w-11 items-center justify-center rounded-md bg-blue-500 text-white"><Activity size={24} strokeWidth={2.5} /></div>}
            <div>
              <div className="font-display text-2xl font-extrabold tracking-tight" data-testid="login-brand">{settings.app_name}</div>
              <div className="text-xs text-slate-500 uppercase tracking-[0.2em]">{settings.company_name || settings.tagline || "Manufacturing Performance Control"}</div>
            </div>
          </div>
          <div className="pp-card p-6 space-y-4">
            <div>
              <label className="pp-label block mb-1.5">Email</label>
              <input className="pp-input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@plant.com" required data-testid="login-email-input" />
            </div>
            <div>
              <label className="pp-label block mb-1.5">Password</label>
              <input className="pp-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" />
            </div>
            {error && <div className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={busy} className="pp-btn-primary w-full" data-testid="login-submit-btn"><LogIn size={16} /> {busy ? "Signing in…" : "Sign in"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
